// frontend/src/data/questions.js
/**
 * AI FINE PROTECTOR™ - MASTER QUESTION BANK
 * Version: 2026.1 (Actuarial Weighted)
 *
 * Notes:
 * - Numeric IDs are preserved for backward compatibility with stored answers + remediation map.
 * - `jurisdictions` is additive and enables multi-jurisdiction overlays (not assessment filtering).
 * - Use "ALL" when a control is broadly defensible regardless of jurisdiction.
 * - Guidance is provided for every question to orient the user consistently.
 *
 * Gravity Scale (UPDATED - Route A):
 * - We rescale the prior tier-weight values by ×2 to better match the UI “/10” convention.
 * - This does NOT change proportional allocation outcomes because all values are multiplied uniformly.
 *   Prior: Prohibited 5, High-Risk 3, Governance 2, Transparency 1
 *   New:   Prohibited 10, High-Risk 6, Governance 4, Transparency 2
 */

export const AUDIT_QUESTIONS = [
  // --- TIER 1: PROHIBITED (GRAVITY 10) - TRIGGERS MAX PENALTIES ---
  {
    id: 1,
    title: "Biometric inference of protected traits",
    text: "Does the system use biometric data to infer protected traits (race, religion, etc.)?",
    gravity: 10,
    category: "Prohibited",
    jurisdictions: ["ALL"],
    guidance:
      "If your model infers sensitive traits from biometrics (face/voice/fingerprint), this is typically treated as extremely high-risk or prohibited.",
  },
  {
    id: 2,
    title: "Social scoring",
    text: "Is the AI used for 'Social Scoring' or evaluative classification by a private/public entity?",
    gravity: 10,
    category: "Prohibited",
    jurisdictions: ["ALL"],
    guidance:
      "Social scoring means assigning reputational or behavioral scores that influence access to services, opportunities, or treatment.",
  },
  {
    id: 3,
    title: "Emotion recognition in workplace/education",
    text: "Does the system use 'Emotion Recognition' in a workplace or educational setting?",
    gravity: 10,
    category: "Prohibited",
    jurisdictions: ["ALL"],
    guidance:
      "Emotion recognition includes detecting mood/engagement/stress from face, voice, keystrokes, or behavior in work/school contexts.",
  },
  {
    id: 4,
    title: "Subliminal manipulation",
    text: "Does the AI employ subliminal techniques to manipulate human behavior?",
    gravity: 10,
    category: "Prohibited",
    jurisdictions: ["ALL"],
    guidance:
      "This includes covert nudges designed to bypass informed choice or materially distort behavior without meaningful awareness.",
  },
  {
    id: 5,
    title: "Untargeted facial scraping",
    text: "Is the system used for untargeted scraping of facial images from CCTV or the internet?",
    gravity: 10,
    category: "Prohibited",
    jurisdictions: ["ALL"],
    guidance:
      "Untargeted scraping means collecting facial images at scale from public sources without a specific, lawful, documented basis.",
  },
  {
    id: 6,
    title: "Exploitation of vulnerable groups",
    text: "Does the AI exploit vulnerabilities of specific groups (age, disability, socio-economic status)?",
    gravity: 10,
    category: "Prohibited",
    jurisdictions: ["ALL"],
    guidance:
      "Exploitation includes targeting or manipulating vulnerable individuals in ways likely to cause harm or materially impair autonomy.",
  },
  {
    id: 7,
    title: "Predictive policing via profiling",
    text: "Is the AI used for 'Predictive Policing' based solely on profiling or personality traits?",
    gravity: 10,
    category: "Prohibited",
    jurisdictions: ["ALL"],
    guidance:
      "Predictive policing via profiling includes scoring individuals as likely offenders based on traits rather than specific evidence.",
  },
  {
    id: 8,
    title: "Real-time remote biometric identification",
    text: "Does the system perform real-time remote biometric identification in public spaces?",
    gravity: 10,
    category: "Prohibited",
    jurisdictions: ["ALL"],
    guidance:
      "This includes identifying people live via face/voice biometrics in public spaces, even if the match is probabilistic.",
  },
  {
    id: 9,
    title: "Deceptive UX defeating informed choice",
    text: "Is the system used to bypass an individual's 'Informed Decision' through deceptive UX design?",
    gravity: 10,
    category: "Prohibited",
    jurisdictions: ["ALL"],
    guidance:
      "Deceptive UX (dark patterns) includes interfaces designed to trick users into consenting or sharing data against their intent.",
  },
  {
    id: 10,
    title: "Political viewpoint discrimination",
    text: "Does the model demonstrate 'Political Viewpoint Discrimination' in its outputs? (TX HB 149 Requirement)",
    gravity: 10,
    category: "Prohibited",
    jurisdictions: ["US", "ALL"],
    guidance:
      "Assess whether the system treats users differently or suppresses content based on political viewpoint in outputs or ranking.",
  },

  // --- TIER 2: HIGH-RISK (GRAVITY 6) - REQUIRES INTENSIVE AUDIT ---
  {
    id: 11,
    title: "Critical infrastructure safety component",
    text: "Is the AI used as a safety component for critical infrastructure (Water, Power, Transport)?",
    gravity: 6,
    category: "High-Risk",
    jurisdictions: ["ALL"],
    guidance:
      "If AI outputs can directly affect safety or continuity of critical services, controls and documentation typically escalate sharply.",
  },
  {
    id: 12,
    title: "Employment decisions",
    text: "Is the AI used for recruitment, CV screening, or firing decisions?",
    gravity: 6,
    category: "High-Risk",
    jurisdictions: ["ALL"],
    guidance:
      "Employment use includes screening, ranking, interviewing, performance scoring, promotion, or termination recommendations.",
  },
  {
    id: 13,
    title: "Creditworthiness or insurance pricing",
    text: "Is the system used to determine creditworthiness or insurance premiums?",
    gravity: 6,
    category: "High-Risk",
    jurisdictions: ["ALL"],
    guidance:
      "This includes approvals/denials, pricing, limits, eligibility, or underwriting decisions driven by model outputs.",
  },
  {
    id: 14,
    title: "Judicial or democratic processes",
    text: "Does the AI assist in judicial or democratic processes (elections/court rulings)?",
    gravity: 6,
    category: "High-Risk",
    jurisdictions: ["ALL"],
    guidance:
      "Includes tools influencing sentencing, bail, legal outcomes, voter targeting, election content, or political persuasion operations.",
  },
  {
    id: 15,
    title: "Medical diagnostics / triage",
    text: "Is the system used for medical diagnostics or healthcare triage?",
    gravity: 6,
    category: "High-Risk",
    jurisdictions: ["ALL"],
    guidance:
      "If the model influences diagnosis, treatment prioritization, clinical decisions, or patient risk scoring, oversight requirements increase.",
  },
  {
    id: 16,
    title: "High-compute frontier threshold",
    text: "Does the system process 'High-Compute' frontier data (>10^26 FLOPs)? (CA SB 53 Threshold)",
    gravity: 6,
    category: "High-Risk",
    jurisdictions: ["US", "ALL"],
    guidance:
      "Frontier/high-compute systems may trigger special governance expectations, including safety testing and incident reporting readiness.",
  },
  {
    id: 17,
    title: "Human-in-the-loop for consequential outputs",
    text: "Is there a 'Human-in-the-Loop' (HITL) mechanism for every consequential output?",
    gravity: 6,
    category: "High-Risk",
    jurisdictions: ["ALL"],
    guidance:
      "HITL means a qualified human reviews or can override AI outputs before they materially affect individuals or safety outcomes.",
  },
  {
    id: 18,
    title: "Formal DPIA conducted",
    text: "Have you conducted a formal Data Protection Impact Assessment (DPIA) for this AI system?",
    gravity: 6,
    category: "High-Risk",
    jurisdictions: ["EU", "UK", "ALL"],
    guidance:
      "A DPIA assesses privacy risks, mitigations, lawful basis, and residual risk before deployment of higher-risk processing.",
  },
  {
    id: 19,
    title: "Law enforcement risk assessments",
    text: "Is the AI used for law enforcement risk assessments of individuals?",
    gravity: 6,
    category: "High-Risk",
    jurisdictions: ["ALL"],
    guidance:
      "Includes systems scoring individuals for risk, threat, recidivism, surveillance prioritization, or investigative targeting.",
  },
  {
    id: 20,
    title: "Access to essential services",
    text: "Does the system facilitate access to essential private or public services?",
    gravity: 6,
    category: "High-Risk",
    jurisdictions: ["ALL"],
    guidance:
      "Essential services include housing, utilities, education, healthcare, banking, benefits, or similar necessities.",
  },

  // --- TIER 3: GOVERNANCE (GRAVITY 4) - THE COMPLIANCE TRAIL ---
  {
    id: 21,
    title: "ROPA maintained for this AI",
    text: "Do you maintain a 'Record of Processing Activities' (ROPA) specifically for this AI?",
    gravity: 4,
    category: "Governance",
    jurisdictions: ["EU", "UK", "ALL"],
    guidance:
      "A ROPA is a documented inventory of processing purposes, data categories, recipients, retention, and security measures.",
  },
  {
    id: 22,
    title: "Kill switch / shutdown protocol",
    text: "Is there a 'Kill Switch' or immediate shutdown protocol for the system?",
    gravity: 4,
    category: "Governance",
    jurisdictions: ["ALL"],
    guidance:
      "A kill switch enables rapid disabling of model features or deployments during incidents or unsafe behavior.",
  },
  {
    id: 23,
    title: "Drift + performance monitoring logs",
    text: "Are there automated logs for system performance and 'drift' monitoring?",
    gravity: 4,
    category: "Governance",
    jurisdictions: ["ALL"],
    guidance:
      "Drift monitoring tracks changes in model inputs/outputs over time and alerts when accuracy or behavior degrades.",
  },
  {
    id: 24,
    title: "Bias audit of training data",
    text: "Have you conducted a bias audit on your training datasets?",
    gravity: 4,
    category: "Governance",
    jurisdictions: ["ALL"],
    guidance:
      "Bias audits typically check representativeness, disparate impact risk, and documented mitigation steps.",
  },
  {
    id: 25,
    title: "Whistleblower channel for AI safety",
    text: "Is there a formal 'Whistleblower' channel for AI-safety concerns? (CA SB 53 Requirement)",
    gravity: 4,
    category: "Governance",
    jurisdictions: ["US", "ALL"],
    guidance:
      "A whistleblower channel allows staff to report AI safety concerns without retaliation, with documented intake and follow-up.",
  },
  {
    id: 26,
    title: "Technical documentation file maintained",
    text: "Does the system have a 'Technical Documentation' file (Annex IV compliant)?",
    gravity: 4,
    category: "Governance",
    jurisdictions: ["EU", "UK", "ALL"],
    guidance:
      "Technical documentation typically includes system description, design choices, testing, risk controls, and intended use limits.",
  },
  {
    id: 27,
    title: "Registered in high-risk public database",
    text: "Is the AI registered in the EU/National public database for high-risk systems?",
    gravity: 4,
    category: "Governance",
    jurisdictions: ["EU"],
    guidance:
      "Some regimes require registration of certain high-risk systems; confirm whether your use case triggers that obligation.",
  },
  {
    id: 28,
    title: "Protection of model weights",
    text: "Are there cybersecurity measures to protect 'Model Weights' from theft?",
    gravity: 4,
    category: "Governance",
    jurisdictions: ["ALL"],
    guidance:
      "Controls include access restrictions, encryption, secrets management, and monitoring for exfiltration attempts.",
  },
  {
    id: 29,
    title: "NIST AI RMF alignment",
    text: "Does the system meet NIST AI Risk Management Framework (RMF) standards?",
    gravity: 4,
    category: "Governance",
    jurisdictions: ["US", "ALL"],
    guidance:
      "NIST AI RMF alignment means documented governance, mapping, measurement, and management of AI risks across lifecycle.",
  },
  {
    id: 30,
    title: "Post-market monitoring plan",
    text: "Do you have a 'Post-Market Monitoring' plan in place?",
    gravity: 4,
    category: "Governance",
    jurisdictions: ["EU", "UK", "ALL"],
    guidance:
      "Post-market monitoring documents how you detect issues after deployment and how you remediate and communicate changes.",
  },

  // --- TIER 4: TRANSPARENCY (GRAVITY 2) - CONSUMER RIGHTS ---
  {
    id: 31,
    title: "AI interaction notice",
    text: "Are users explicitly notified they are interacting with an AI (e.g., Chatbots)?",
    gravity: 2,
    category: "Transparency",
    jurisdictions: ["ALL"],
    guidance:
      "Notice should be clear and timely, explaining that AI is involved and how users can reach a human when appropriate.",
  },
  {
    id: 32,
    title: "Synthetic media labeling / watermarking",
    text: "Is AI-generated content (synthetic media/deepfakes) watermarked or labeled?",
    gravity: 2,
    category: "Transparency",
    jurisdictions: ["ALL"],
    guidance:
      "Labeling helps prevent deception and supports provenance; apply to images, audio, video, and AI-written content as relevant.",
  },
  {
    id: 33,
    title: "Human-readable model summary",
    text: "Is there a 'Human-Readable' summary of the model's capabilities and risks?",
    gravity: 2,
    category: "Transparency",
    jurisdictions: ["ALL"],
    guidance:
      "A short summary should cover intended use, limitations, key risks, and safe operating constraints for stakeholders.",
  },
  {
    id: 34,
    title: "Explanation for AI-driven decisions",
    text: "Can users request an explanation for a specific AI-driven decision?",
    gravity: 2,
    category: "Transparency",
    jurisdictions: ["EU", "UK", "ALL"],
    guidance:
      "Users should be able to understand what factors contributed to an outcome and how to contest or correct errors.",
  },
  {
    id: 35,
    title: "Opt-out of profiling / training",
    text: "Is there a mechanism for users to opt-out of 'Profiling' or data training?",
    gravity: 2,
    category: "Transparency",
    jurisdictions: ["EU", "UK", "ALL"],
    guidance:
      "Opt-out should be accessible, documented, and honored operationally across data pipelines and downstream vendors.",
  },
  {
    id: 36,
    title: "Model release date + data vintage disclosed",
    text: "Does the model clearly disclose its 'Release Date' and training data vintage?",
    gravity: 2,
    category: "Transparency",
    jurisdictions: ["ALL"],
    guidance:
      "Disclosing recency helps users calibrate trust and supports auditability when outputs rely on time-sensitive knowledge.",
  },
  {
    id: 37,
    title: "Instructions for deployers in local language",
    text: "Are instructions for use provided to 'Deployers' in their local language?",
    gravity: 2,
    category: "Transparency",
    jurisdictions: ["EU", "UK", "ALL"],
    guidance:
      "Deployers should receive clear operational instructions, constraints, and safety steps in a language they can reliably use.",
  },
  {
    id: 38,
    title: "Avoids dark patterns",
    text: "Does the model avoid 'Dark Patterns' designed to trick consumers into data sharing?",
    gravity: 2,
    category: "Transparency",
    jurisdictions: ["ALL"],
    guidance:
      "Avoid interfaces that nudge users into unintended consent, hide options, or make privacy-protective choices difficult.",
  },
  {
    id: 39,
    title: "Public disclosure of objective",
    text: "Is the AI's intent and objective clearly defined in public disclosures?",
    gravity: 2,
    category: "Transparency",
    jurisdictions: ["ALL"],
    guidance:
      "State purpose and boundaries so users and regulators can evaluate whether deployment matches stated intent.",
  },
  {
    id: 40,
    title: "Responsible AI training for employees",
    text: "Have employees been trained on 'Responsible AI' and 'Regulatory Compliance'?",
    gravity: 2,
    category: "Transparency",
    jurisdictions: ["ALL"],
    guidance:
      "Training should cover safe usage, escalation, privacy/security handling, and how to document decisions and incidents.",
  },

  // --- EXPANSION (QUESTIONS 41–60): DEPTH CONTROLS FOR 60-Q BANK ---

  // --- RECORD KEEPING / AUDITABILITY ---
  {
    id: 41,
    title: "Model card / system card maintained",
    text: "Do you maintain a model card (or system card) documenting intended use, limitations, and known risks?",
    gravity: 4,
    category: "Governance",
    jurisdictions: ["ALL"],
    guidance:
      "A model/system card is a concise, versioned document used for governance and audit trails.",
  },
  {
    id: 42,
    title: "Change management for models and prompts",
    text: "Is there a formal change management process for model updates, prompt changes, and configuration changes?",
    gravity: 4,
    category: "Governance",
    jurisdictions: ["ALL"],
    guidance:
      "Track changes with approvals, rollbacks, and dated release notes.",
  },
  {
    id: 43,
    title: "Retention policy for logs and outputs",
    text: "Do you have a documented retention policy for AI logs, outputs, and user interactions aligned to legal requirements?",
    gravity: 4,
    category: "Governance",
    jurisdictions: ["EU", "UK", "US", "ALL"],
    guidance:
      "Define what you retain, for how long, who can access it, and how you secure and delete it defensibly.",
  },
  {
    id: 44,
    title: "Audit-ready evidence repository",
    text: "Do you maintain an audit-ready repository for policies, testing results, and evidence of controls (with owners and dates)?",
    gravity: 4,
    category: "Governance",
    jurisdictions: ["ALL"],
    guidance:
      "Centralize evidence with owners, last-updated dates, and links to source artifacts to support audits and due diligence.",
  },

  // --- DATA GOVERNANCE ---
  {
    id: 45,
    title: "Training data provenance documented",
    text: "Do you document training data provenance, licensing/permissions, and data lineage for the AI system?",
    gravity: 4,
    category: "Governance",
    jurisdictions: ["EU", "UK", "US", "ALL"],
    guidance:
      "Document sources, rights/licenses, collection methods, and lineage to reduce legal and compliance exposure.",
  },
  {
    id: 46,
    title: "PII minimization and data classification",
    text: "Do you classify data used by the AI (PII/sensitive/non-sensitive) and enforce minimization for each use case?",
    gravity: 4,
    category: "Governance",
    jurisdictions: ["EU", "UK", "US", "ALL"],
    guidance:
      "Classify data and restrict collection/use to what is necessary; enforce controls for sensitive categories.",
  },
  {
    id: 47,
    title: "Data subject request handling",
    text: "Do you have a documented process to handle data subject requests related to AI outputs and training data (access, deletion, correction)?",
    gravity: 4,
    category: "Governance",
    jurisdictions: ["EU", "UK", "ALL"],
    guidance:
      "Define intake, verification, timelines, and how requests propagate through vendors, logs, and training pipelines.",
  },

  // --- TECHNICAL ROBUSTNESS / SECURITY ---
  {
    id: 48,
    title: "Red teaming / adversarial testing",
    text: "Have you performed adversarial testing (red teaming) for jailbreaks, prompt injection, and misuse scenarios?",
    gravity: 4,
    category: "Governance",
    jurisdictions: ["ALL"],
    guidance:
      "Test realistic attacker behaviors and document mitigations, residual risk, and retesting cadence.",
  },
  {
    id: 49,
    title: "Secure prompt handling and input controls",
    text: "Do you implement input validation and prompt security controls to reduce prompt injection and data exfiltration risk?",
    gravity: 4,
    category: "Governance",
    jurisdictions: ["ALL"],
    guidance:
      "Use input filtering, content boundaries, tool permissioning, and safe templating to reduce injection risk.",
  },
  {
    id: 50,
    title: "Access control for AI admin functions",
    text: "Are AI administration functions protected by role-based access control (RBAC) and least-privilege permissions?",
    gravity: 4,
    category: "Governance",
    jurisdictions: ["ALL"],
    guidance:
      "Restrict model settings, prompt libraries, logs, and evaluation tools to approved roles with auditable access.",
  },
  {
    id: 51,
    title: "Incident response for AI failures",
    text: "Do you have an incident response plan specifically for AI failures (harm events, hallucinations, data leakage)?",
    gravity: 4,
    category: "Governance",
    jurisdictions: ["ALL"],
    guidance:
      "Define detection, triage, containment, notification triggers, remediation steps, and post-incident review for AI events.",
  },

  // --- HUMAN OVERSIGHT / OPERATIONAL CONTROLS ---
  {
    id: 52,
    title: "Human override and escalation path",
    text: "Is there a clear escalation path and human override for users impacted by AI-driven outcomes?",
    gravity: 4,
    category: "High-Risk",
    jurisdictions: ["EU", "UK", "US", "ALL"],
    guidance:
      "Users should have a clear route to a human decision-maker and a documented process to override or correct outcomes.",
  },
  {
    id: 53,
    title: "Quality assurance sampling of outputs",
    text: "Do you perform routine QA sampling of AI outputs (with thresholds and corrective actions) to control drift and error rates?",
    gravity: 4,
    category: "Governance",
    jurisdictions: ["ALL"],
    guidance:
      "Sample outputs, measure error rates, set thresholds, and document corrective actions when performance degrades.",
  },
  {
    id: 54,
    title: "Policy for high-risk use cases",
    text: "Do you have a policy that defines which AI use cases are allowed, restricted, or prohibited (with approval gates)?",
    gravity: 4,
    category: "Governance",
    jurisdictions: ["ALL"],
    guidance:
      "Define approvals, prohibited uses, and escalation triggers so teams don’t deploy high-risk AI ad hoc.",
  },

  // --- TRANSPARENCY (deeper) ---
  {
    id: 55,
    title: "User-facing disclosure for limitations",
    text: "Do user disclosures clearly explain limitations, likely error modes, and when to seek human assistance?",
    gravity: 2,
    category: "Transparency",
    jurisdictions: ["ALL"],
    guidance:
      "Disclosures should set expectations, warn about error modes, and direct users to human support for consequential outcomes.",
  },
  {
    id: 56,
    title: "Complaint / appeal pathway",
    text: "Is there a user-facing complaint or appeal pathway for AI-driven outcomes?",
    gravity: 2,
    category: "Transparency",
    jurisdictions: ["EU", "UK", "ALL"],
    guidance:
      "Provide a clear path to complain, appeal, and obtain review—especially for decisions affecting rights or opportunities.",
  },

  // --- THIRD-PARTY / VENDOR RISK ---
  {
    id: 57,
    title: "Vendor risk assessment for AI suppliers",
    text: "Do you perform vendor risk assessments for third-party AI providers (security, privacy, compliance, SLAs)?",
    gravity: 4,
    category: "Governance",
    jurisdictions: ["ALL"],
    guidance:
      "Assess vendor controls, security posture, data handling, audit rights, incident response, and contractual obligations.",
  },
  {
    id: 58,
    title: "Contractual controls for AI vendors",
    text: "Do your vendor contracts include AI-specific controls (audit rights, breach notice, data handling, sub-processors)?",
    gravity: 4,
    category: "Governance",
    jurisdictions: ["EU", "UK", "US", "ALL"],
    guidance:
      "Contracts should cover audit/inspection rights, breach notice SLAs, data usage limits, and sub-processor transparency.",
  },

  // --- MODEL BEHAVIOR / SAFETY ---
  {
    id: 59,
    title: "Hallucination and accuracy controls",
    text: "Do you measure hallucination/accuracy rates for critical workflows and apply guardrails (retrieval, citations, constraints)?",
    gravity: 4,
    category: "Governance",
    jurisdictions: ["ALL"],
    guidance:
      "Measure error/hallucination rates, define acceptable thresholds, and implement guardrails (RAG, constraints, reviews).",
  },
  {
    id: 60,
    title: "Prevent sensitive data leakage",
    text: "Do you test and enforce controls to prevent leakage of sensitive data through prompts, outputs, or logs?",
    gravity: 4,
    category: "Governance",
    jurisdictions: ["ALL"],
    guidance:
      "Test for leakage and enforce controls like redaction, secrets handling, access limits, and logging minimization.",
  },
];